/**
 * CameraHealthCheckService
 * 
 *   1. Čierny obraz - kamera nefunguje alebo je zakrytá
 *   2. Zamrznutý stream - kamera prestala streamovať nové snímky
 *   3. Nedostupné video - chyba z face alebo dokument knižnice
 * 
 * Pri detekcii poruchy zobrazí notifikáciu a naplánuje automatický reštart obrazovky.
 * 
 * Použitie:
 *   - Volá sa pri štarte biometrie a onboardingu
 *   - Debug režim zobrazuje živé video a vzorkované pixely pre diagnostiku
 */
angular.module('screensApp')
    .service('CameraHealthCheckService', function($rootScope, CameraHealthCheckDebugService) {

        // Uloženie this do self pre použitie v inner funkciách
        var self = this;

        // ============================================
        // KONFIGURÁCIA A KONŠTANTY
        // ============================================
        
        // Debug mód - zobrazenie živého videa a vzorkovaných canvasov pre diagnostiku
        // Zapína sa cez config.EnableCameraHealthCheckDebugMode alebo kontextové menu hornej obrazovky pokiaľ som na STAGING
        var debugMode = $rootScope.config.EnableCameraHealthCheckDebugMode || false;

        // Flag indikujúci, či je už naplánovaný reštart (zabráni viacnásobným notifikáciám)
        self.cameraHealthCheckRestartPlanned = false;

        // Čas v sekundách pred reštartom a na toast notifikáciu pred reštartom obrazovky
        var restartDelay = 5;
        
        // Prah svetlosti pre považovanie pixelu za čierny (RGB hodnoty < 15)
        const lightnessThreshold = 15;
        
        // Minimálne percento čiernych pixelov pre označenie snímku ako čierneho (90%)
        const percentageThresholdBlack = 90;
        
        // Vzorkovanie každého 10. pixelu pri kontrole čierneho obrazu
        // Nižšie číslo = presnejšia detekcia, ale pomalšia
        const sampleThresholdBlack = 10;
        
        // Vzorkovanie každého 50. pixelu pri kontrole zamrznutého streamu
        // Vyššie číslo = rýchlejšia kontrola, ale menej citlivá na malé zmeny
        const sampleThresholdFrozen = 50;
        
        // Počet po sebe idúcich kontrol s čiernym snímkom pred reštartom (3 x 1s = 3 sekndy)
        const blackFrameThreshold = 3;
        
        // Počet po sebe idúcich kontrol s rovnakým snímkom = zamrznutie (3 x 1s = 3 sekndy)
        // Malo by byť rovnaké alebo väčšie ako blackFrameThreshold aby sa zaznamenal správny dôvod reštartu
        const freezeThreshold = blackFrameThreshold + 0;
        
        // Maximálna doba monitorovania zamrznutého streamu (5 sekúnd)
        // Po tomto čase sa monitoring automaticky zastaví, ak nenastane porucha
        const maxFrozenDuration = 5000;
        
        // Interval kontroly zamrznutého streamu v milisekundách
        // ImageCapture API je veľmi rýchle, kontrolujeme každých 300ms
        const checkInterval = 300;

        // Maximálna doba čakania na inicializáciu video elementu a jeho rozmerov (10 sekúnd)
        const maxVideoNotReadyDuration = 10000;
                
        // Počet po sebe idúcich kontrol s disabled track pred reštartom (3 x `checkInterval` = 900ms)
        // Krátky čas pre rýchlu detekciu technického problému kamery
        const disabledTrackThreshold = 3;
        
        // Počet po sebe idúcich kontrol s muted track pred reštartom (5 x `checkInterval` = 1.5s)
        // muted = true znamená že hardware neposkytuje dáta (kamera zamrzla, obraz zostal)
        const mutedTrackThreshold = 5;

        // ============================================
        // FUNKCIA PRE NOTIFIKÁCIU A RESTART
        // ============================================
        
        /**
         * Zobrazí toast notifikáciu s odpočítavaním a naplánuje automatický reštart obrazovky
         * 
         * @param {string} reason - Dôvod reštartu ('black-stream', 'frozen-stream' alebo 'stream-unavailable')
         * 
         * Priebeh:
         *   1. Odošle info o reštarte na spodnú obrazovku
         *   2. Zobrazí toast s odpočítavaním (5 sekúnd)
         *   3. Aktualizuje toast každú sekundu
         *   4. Po vypršaní času vykoná ReloadTop() alebo location.reload()
         */
        self.handleNotifyAndRestart = function(reason) {

            // Ak je už naplánovaný reštart, zabráň viacnásobným notifikáciám
            if (self.cameraHealthCheckRestartPlanned) {
                $rootScope.config.DebugJsConsoleLogEnabled && console.warn('🎥 Reštart už naplánovaný, ignorujem ďalšie volania');
                return;
            }

            $rootScope.config.DebugJsConsoleLogEnabled && console.warn('🎥 Detekovaná porucha kamery:', reason);
            $rootScope.config.DebugJsConsoleLogEnabled && console.warn('🎥 Plánovanie reštartu obrazovky za', restartDelay, 'sekúnd');

            // odošlem info o reštarte na spodnú obrazovku
            // Zaloguje cez spodnú do GA. Dôvod môže byť 'black-stream', 'frozen-stream' alebo 'stream-unavailable'
            $rootScope.BottomScreen("biometryAutentification", { state: "restarting", infoGA: reason });
            // zaloguje do kibany
            $rootScope.createLog(2, 'cameraHealthCheck() restart', '# ERROR: ' + reason);

            debugMode && $rootScope.toggleToastNotification({show:true, text: "Camera health check RESTARTING due to: <mark>" + reason + "</mark>", id:"cameraHealthCheck-restarting" + Date.now(), timeout: 20});

            // Nastav premennú, že je naplánovaný reštart
            self.cameraHealthCheckRestartPlanned = true;

            // Aktualizovateľná správa pre toast notifikáciu
            const getToastMessage = (seconds) => $rootScope.translations.biometry[$rootScope.config.LanguageId]?.cameraFailedToast + ` ${seconds}s`;

            // Zobraz počiatočnú notifikáciu pre hráča na hornej obrazovke
            $rootScope.toggleToastNotification({
                show: true,
                text: getToastMessage(restartDelay),
                id: "cameraHealthCheck-toast",
                timeout: restartDelay,
                icon: "alert alert-reload-top"
            });

            // Aktualizuj odpočítavanie každú sekundu
            const countdownInterval = setInterval(() => {
                restartDelay--;

                if (restartDelay <= 0) {
                    clearInterval(countdownInterval);
                    $rootScope.config.DebugJsConsoleLogEnabled && console.warn('🎥 Vykonávam reštart obrazovky...');
                    try {
                        ReloadTop(); // volanie na native app
                    } catch (error) {
                        console.error('🎥 Chyba pri ReloadTop(), pokúšam sa o location.reload()', error);
                        location.reload(); // fallback pre browser
                    }
                } else {
                    // Aktualizuj toast notifikáciu s novým časom
                    $rootScope.toggleToastNotification({
                        show: true,
                        text: getToastMessage(restartDelay),
                        id: "cameraHealthCheck-toast"
                    });
                }
            }, 1000);
        };

        // ============================================
        // HLAVNÁ FUNKCIA PRE KONTROLU streamu KAMERY
        // ============================================
        
        /**
         * Hlavná vstupná funkcia pre kontrolu streamu kamery
         * Volá sa automaticky pri štarte kamery (event: face-auto-capture:state-changed)
         * 
         * @param {HTMLVideoElement} videoElement - Video element s kamerovým streamom
         * @returns {boolean|undefined} false ak je reštart už naplánovaný, inak undefined
         * 
         * Vykonáva:
         *   1. Okamžitú kontrolu čierneho obrazu
         *   2. Spustenie kontinuálneho monitoringu zamrznutia (beží na pozadí)
         *   3. Ak je detekovaný čierny obraz, naplánuje reštart
         */
        self.start = function(videoElement) {

            // Ak nie je povolená kontrola kamery v konfigurácii, preskočíme
            if (!$rootScope.config.IsCameraHealthCheckEnabled) {
                $rootScope.config.DebugJsConsoleLogEnabled && console.log('🎥 Kontrola kamery je vypnutá v konfigurácii');
                return;
            }
            
            // Ak už beží reštart, nepokračujeme v ďalších kontrolách
            if (self.cameraHealthCheckRestartPlanned) {
                return false;
            }
            
            // kontrola debugMode ak bol zapnutý po nacitani aplikacie
            debugMode = $rootScope.config.EnableCameraHealthCheckDebugMode;
            
            // Kontrola, či video element existuje
            if (!videoElement) {
                console.warn('🎥 Video element neexistuje - kamera môže byť použitá inou aplikáciou alebo ešte nebola inicializovaná');
                // Spustíme monitoring, ktorý bude kontrolovať, či sa video element vytvorí
                self.startFrozenStreamMonitoring(null);
                return;
            } else {
                // Spustenie kontinuálneho monitoringu
                // Monitoring sám skontroluje rozmery videa, čierny obraz a zamrznutie streamu
                self.startFrozenStreamMonitoring(videoElement);
            }
        };

        // ============================================
        // DETEKCIA  ČIERNEHO A ZAMRZNUTÉHO STREAMU
        // ============================================
        
        // Privátne premenné pre monitoring zamrznutého streamu
        var frozenMonitoringActive = false; // Flag, či už monitoring beží
        var frozenCheckInterval = null;     // Referencia na setInterval
        var imageCapture = null;             // ImageCapture API inštancia

        /**
         * Spustenie kontinuálneho monitorovania zamrznutého streamu
         * Beží na pozadí každých checkInterval a porovnáva frame signatúry
         * 
         * @param {HTMLVideoElement} videoElement - Video element s kamerovým streamom
         * 
         * Algoritmus:
         *   1. Každých checkInterval získa aktuálny snímok cez ImageCapture.grabFrame()
         *   2. Vzorkuje každý 50. pixel (len červený kanál)
         *   3. Spočíta "frame signatúru" (súčet červených hodnôt vzorkovaných pixelov)
         *   4. Porovná s predchádzajúcou signatúrou
         *   5. Ak sú 3x po sebe rovnaké → stream je zamrznutý
         *   6. Automaticky sa zastaví po 5 sekundách alebo pri reštarte
         * 
         * Debug režim:
         *   - Zobrazí vzorkované pixely ako canvas vpravo hore
         *   - Môže simulovať zamrznutie kamery po N sekundách
         */
        self.startFrozenStreamMonitoring = function(videoElement) {
                
            // Ak už monitoring beží, zastavíme ho a resetujeme pre nový štart
            if (frozenMonitoringActive && videoElement !== null) {
                $rootScope.config.DebugJsConsoleLogEnabled && console.log('🎥 Monitoring zamrznutia už beží - zastavujem ho a spúšťam nový');
                self.stopFrozenStreamMonitoring();
            }

            debugMode && $rootScope.toggleToastNotification({show:true, text: "Camera health check <mark>STARTED</mark>", id:"cameraHealthCheck-startStop", timeout: 3});
            $rootScope.config.DebugJsConsoleLogEnabled && console.log('🎥 Spúšťam monitoring zamrznutého streamu (max. ' + (maxFrozenDuration / 1000) + 's, interval: ' + checkInterval + 'ms)');

            // ============================================
            // POČIATOČNÁ KONTROLA STAVU STREAMU
            // ============================================
            // Kontrola MediaStreamTrack properties pred spustením monitoringu
            // Podľa reálnych chýb z chyby-streamu-z-kamery.js
            if (videoElement && videoElement.srcObject) {
                const stream = videoElement.srcObject;
                const track = stream.getVideoTracks()[0];
                
                if (track) {
                    $rootScope.config.DebugJsConsoleLogEnabled && console.log('🎥 Počiatočný stav track:', {
                        readyState: track.readyState,
                        muted: track.muted,
                        enabled: track.enabled,
                        label: track.label
                    });
                    
                    // PRÍPAD 1: Stream ukončený (readyState="ended") → čierna obrazovka po reloade
                    if (track.readyState === 'ended') {
                        self.handleVideoNotAvailable('🎥 OKAMŽITÁ DETEKCIA: Track je ukončený (readyState=ended) - kamera nie je dostupná');
                        return;
                    }
                    
                    // PRÍPAD 2: Stream zamrznutý (muted=true && readyState="live")
                    if (track.muted === true && track.readyState === 'live') {
                        console.error('🎥 OKAMŽITÁ DETEKCIA: Track je zamrznutý (muted=true) - kamera prestala poskytovať dáta');
                        self.handleFrozenStream('frozen-stream');
                        return;
                    }
                    
                    // PRÍPAD 3: Track deaktivovaný (enabled=false)
                    if (track.enabled === false) {
                        console.error('🎥 OKAMŽITÁ DETEKCIA: Track je deaktivovaný (enabled=false) - technický problém');
                        self.handleFrozenStream('stream-unavailable');
                        return;
                    }
                    
                    // Stream vyzerá v poriadku, pokračujeme s monitoringom
                    $rootScope.config.DebugJsConsoleLogEnabled && console.log('🎥 Počiatočná kontrola OK - stream vyzerá byť funkčný');
                }
            }

            // Premenné pre sledovanie zmien snímkov
            let lastFrameData = null;
            let frozenFrameCount = 0;
            let blackFrameCount = 0; // Počítadlo čiernych framov
            let disabledTrackCount = 0; // Počítadlo disabled track (technický problém)
            let mutedTrackCount = 0; // Počítadlo muted track (hardware nefunguje)
            var iteratorCount = 0;
            var videoReadyListenerSet = false; // Flag, či už bol nastavený loadedmetadata listener
            const maxIterations = Math.floor(maxFrozenDuration / checkInterval); // 5000ms / checkInterval = 16 iterácií
            
            // Canvas pre porovnávanie snímkov
            const canvas = document.getElementById("camera-health-check-canvas-frozen-stream-monitoring") || document.createElement('canvas');
            const context = canvas.getContext('2d', { willReadFrequently: true });

            canvas.id = "camera-health-check-canvas-frozen-stream-monitoring";

            frozenMonitoringActive = true;
            
            // Spustenie intervalu na kontrolu zamrznutia a čierneho obrazu
            // Kontrola každú sekundu od startu monitoringu - automaticky sa zastaví po reštarte alebo po 20 iteráciách
            frozenCheckInterval = setInterval(async () => {
                
                // kontrola debugMode ak bol zapnutý po inicializácii
                debugMode = $rootScope.config.EnableCameraHealthCheckDebugMode;
                
                // Ak je naplánovaný reštart, zastav monitoring
                if (self.cameraHealthCheckRestartPlanned === true) {
                    $rootScope.config.DebugJsConsoleLogEnabled && console.log('🎥 Reštart naplánovaný, zastavujem monitoring');
                    self.stopFrozenStreamMonitoring();
                    return;
                }

                // Kontrola, či video element existuje
                if (!videoElement) {
                    console.warn('🎥 Video element neexistuje - čakám na vytvorenie');
                    // Debug toast
                    debugMode && $rootScope.toggleToastNotification({ show: true, text: "⏳ Video element zatiaľ neexistuje. Čakám...", id: "cameraHealthCheck-videoReady", timeout: maxVideoNotReadyDuration / 1000});
                    
                    // Pri prvej detekcii nastavíme timeout
                    if (!videoReadyListenerSet) {
                        videoReadyListenerSet = true;
                        setTimeout(() => {
                            if (!videoElement) {
                                self.handleVideoNotAvailable('🎥 Video element neexistuje ani po ' + (maxVideoNotReadyDuration / 1000) + 's - kamera je pravdepodobne použitá inou aplikáciou');
                            }
                        }, maxVideoNotReadyDuration);
                    }
                    return;
                }

                // Kontrola, či má video rozmery
                if (videoElement.videoWidth === 0 || videoElement.videoHeight === 0) {
                    console.warn('🎥 Video element nemá rozmery - čakám na loadedmetadata');
                    // Debug toast
                    debugMode && $rootScope.toggleToastNotification({ show: true, text: "⏳ Video nemá rozmery. Čakám na loadedmetadata...",id: "cameraHealthCheck-videoReady",  timeout: maxVideoNotReadyDuration / 1000 });

                    // Pri prvej detekcii nastavíme listener s timeout fallbackom
                    if (!videoReadyListenerSet) {
                        videoReadyListenerSet = true;
                        
                        const videoReadyTimeout = setTimeout(() => {
                            if (videoElement.videoWidth === 0 || videoElement.videoHeight === 0) {
                                self.handleVideoNotAvailable('🎥 Video nemá rozmery ani po ' + (maxVideoNotReadyDuration / 1000) + 's - kamera je pravdepodobne použitá inou aplikáciou');
                            }
                        }, maxVideoNotReadyDuration);
                        
                        // Event loadedmetadata sa spustí okamžite, keď video získa rozmery
                        videoElement.addEventListener('loadedmetadata', function onVideoReady() {
                            clearTimeout(videoReadyTimeout);
                            videoReadyListenerSet = false; // Povoľ ďalšie checky
                            $rootScope.config.DebugJsConsoleLogEnabled && console.log('🎥 Video element získal rozmery ' + videoElement.videoWidth + 'x' + videoElement.videoHeight + ' (loadedmetadata event)');
                            // Debug toast
                            debugMode && $rootScope.toggleToastNotification({ show: true, text: "Video má rozmery: <code>" + videoElement.videoWidth + "x" + videoElement.videoHeight + "</code>", id: "cameraHealthCheck-videoReady", timeout: 3 });
                        }, { once: true });
                        
                        $rootScope.config.DebugJsConsoleLogEnabled && console.log('🎥 Video ešte nemá rozmery - čakám na loadedmetadata event (max ' + (maxVideoNotReadyDuration / 1000) + 's)');
                    }
                    return;
                }
                //Video element existuje a má rozmery - teraz môžeme začať počítať iterácie pre timeout
                iteratorCount++;
                $rootScope.config.DebugJsConsoleLogEnabled && console.log('🎥 Kontrola streamu kamery - iterácia', iteratorCount, '/', maxIterations);
                
                // Nastav counter na automatické zastavenie monitoringu (len keď už beží skutočná kontrola)
                if (iteratorCount >= maxIterations) {
                    $rootScope.config.DebugJsConsoleLogEnabled && console.warn('🎥 Zastavujem monitoring streamu kamery po ' + (maxFrozenDuration / 1000) + ' sekundách - reset stavu pre možnosť nového štartu');
                    self.stopFrozenStreamMonitoring();
                    self.cameraHealthCheckRestartPlanned = false;
                    return;
                }
                
                // 
                // Inicializuj ImageCapture, ak ešte nie je vytvorený (aj keď video bolo dostupné od začiatku)
                if (!imageCapture && videoElement.srcObject) {
                    const stream = videoElement.srcObject;
                    const track = stream.getVideoTracks()[0];
                    if (track) {
                    imageCapture = new ImageCapture(track);
                    $rootScope.config.DebugJsConsoleLogEnabled && console.log('🎥 ImageCapture API inicializované (interval:', checkInterval + 'ms)');
                    }
                }

                // Nastav rozmery canvasu
                canvas.width = videoElement.videoWidth;
                canvas.height = videoElement.videoHeight;

                try {
                    // Kontrola, či ImageCapture existuje pred použitím
                    if (!imageCapture) {
                        $rootScope.config.DebugJsConsoleLogEnabled && console.warn('🎥 ImageCapture ešte nie je inicializované, preskakujem túto iteráciu');
                        return;
                    }
                    
                    // Kontrola, či je video track stále aktívny
                    const stream = videoElement.srcObject;
                    if (!stream) {
                        self.handleVideoNotAvailable('🎥 Video stream už nie je dostupný');
                        return;
                    }
                    
                    const track = stream.getVideoTracks()[0];
                    if (!track || track.readyState !== 'live') {
                        self.handleVideoNotAvailable('🎥 Video track nie je aktívny (readyState: ' + track?.readyState + ')');
                        return;
                    }
                    
                    // Kontrola technického problému kamery pomocou MediaStreamTrack properties
                    // enabled=false: track je vypnutý, nerenduje stream (standard mute functionality)
                    // muted=true: hardware neposkytuje dáta, kamera zamrzla (technický problém)
                    // Podľa MDN: "muted refers to a condition in which there's no media because of a technical issue"
                    
                    if (track.enabled === false) {
                        disabledTrackCount++;
                        console.warn('🎥 Video track je disabled (vypnutý)! Count:', disabledTrackCount, '/', disabledTrackThreshold);
                        
                        if (disabledTrackCount >= disabledTrackThreshold) {
                            console.error('🎥 TECHNICKÝ PROBLÉM! Track je disabled', disabledTrackCount, 'krát po sebe');
                            self.handleFrozenStream('camera-track-disabled');
                            return;
                        }
                    } else {
                        // Track je enabled, resetuj počítadlo
                        if (disabledTrackCount > 0) {
                            $rootScope.config.DebugJsConsoleLogEnabled && console.log('🎥 Video track je opäť enabled');
                            disabledTrackCount = 0;
                        }
                    }
                    
                    // Kontrola muted stavu - kamera zamrzla, obraz zostal (nie čierny)
                    if (track.muted === true) {
                        mutedTrackCount++;
                        console.warn('🎥 Video track je MUTED (hardware problém - obraz zamrzol)! Count:', mutedTrackCount, '/', mutedTrackThreshold);
                        
                        if (mutedTrackCount >= mutedTrackThreshold) {
                            console.error('🎥 KAMERA ZAMRZLA! Track je muted', mutedTrackCount, 'krát po sebe (hardware neposkytuje dáta)');
                            self.handleFrozenStream('camera-track-muted');
                            return;
                        }
                    } else {
                        // Track nie je muted, resetuj počítadlo
                        if (mutedTrackCount > 0) {
                            $rootScope.config.DebugJsConsoleLogEnabled && console.log('🎥 Video track už nie je muted');
                            mutedTrackCount = 0;
                        }
                    }
                    
                    // Získaj aktuálny frame cez ImageCapture API
                    const imageBitmap = await imageCapture.grabFrame();
                    
                    // Aktualizuj rozmery canvasu podľa bitmap
                    canvas.width = imageBitmap.width;
                    canvas.height = imageBitmap.height;
                    
                    // Nakresli bitmap na canvas
                    context.drawImage(imageBitmap, 0, 0);
                    
                    // Získaj pixelové dáta
                    const imageData = context.getImageData(0, 0, canvas.width, canvas.height).data;
                    
                    // === KOMBINOVANÁ KONTROLA V JEDNEJ ITERÁCII ===
                    // 1. Kontrola čierneho obrazu (vzorkujeme každý 10. pixel)
                    // 2. Kontrola zamrznutia (vzorkujeme každý 50. pixel pre signatúru)
                    
                    let blackPixelCount = 0;
                    let frameSignature = 0;
                    const sampledPixelsBlack = Math.floor(imageData.length / (sampleThresholdBlack * 4));
                    
                    // Vzorkujeme každý 10. pixel pre kontrolu čiernych pixelov
                    for (let i = 0; i < imageData.length; i += sampleThresholdBlack * 4) {
                        // Získaj červený kanál pixelu - stači na kontrolu svetlosti
                        const r = imageData[i];
                        
                        // Kontrola čierneho pixelu
                        if (r < lightnessThreshold) {
                            blackPixelCount++;
                        }
                        
                        // Každý 5. vzorkovaný pixel (t.j. každý 50.) použijeme aj pre frame signatúru
                        // sampleThresholdFrozen (50) / sampleThresholdBlack (10) = 5
                        if (i % (sampleThresholdFrozen * 4) === 0) {
                            frameSignature += r;
                        }
                    }
                    
                    // Výpočet percenta čiernych pixelov
                    const blackPercentage = (blackPixelCount / sampledPixelsBlack) * 100;
                    
                    // Získaj track stavy pre logging
                    const trackEnabled = stream.getVideoTracks()[0]?.enabled ?? 'unknown';
                    const trackMuted = stream.getVideoTracks()[0]?.muted ?? 'unknown';
                    
                    $rootScope.config.DebugJsConsoleLogEnabled && console.log('🎥 Čierne pixely:', blackPercentage.toFixed(2) + '%', '| Frame signatúra:', frameSignature, '| Predchádzajúca:', lastFrameData, '| Frozen count:', frozenFrameCount, '| Track enabled:', trackEnabled, '| muted:', trackMuted);

                    // Debug: zobrazenie videa a vzorkovaných pixelov
                    if (debugMode) {
                        CameraHealthCheckDebugService.showVideoCanvas(
                            videoElement, 
                            'camera-health-check-video-debug',
                            { top: "0", left: "0", scale: "scale(.4)", transformOrigin: "top left" }
                        );
                        
                        // Canvas pre čierne pixely (vzorkovanie každého 10. pixelu)
                        CameraHealthCheckDebugService.showSampledCanvas(
                            imageData, 
                            canvas.width, 
                            canvas.height, 
                            sampleThresholdBlack,
                            'camera-health-check-black-canvas',
                            { top: "200px", right: "0" }
                        );
                        
                        // Canvas pre zamrznutý stream (vzorkovanie každého 50. pixelu)
                        CameraHealthCheckDebugService.showSampledCanvas(
                            imageData, 
                            canvas.width, 
                            canvas.height, 
                            sampleThresholdFrozen,
                            'camera-health-check-frozen-canvas',
                            { top: "0", right: "0" }
                        );
                    }

                    // === VYHODNOTENIE ZAMRZNUTIA ===
                    // Porovnaj aktuálnu signatúru s predchádzajúcou
                    if (lastFrameData !== null && lastFrameData === frameSignature) {
                        // Signatúry sú rovnaké → snímok sa nezmenil
                        frozenFrameCount++;
                        
                        // Zároveň kontroluj, či je obraz čierny
                        if (blackPercentage > percentageThresholdBlack) {
                            blackFrameCount++;
                        } else {
                            blackFrameCount = 0;
                        }
                        
                        $rootScope.config.DebugJsConsoleLogEnabled && console.warn('🎥 Stream sa nezmenil! Frozen count:', frozenFrameCount, '/', freezeThreshold, '| Black count:', blackFrameCount, '/', blackFrameThreshold);
                        
                        // Ak sú 3x po sebe rovnaké signatúry (3 sekundy) → stream je zamrznutý
                        if (frozenFrameCount >= freezeThreshold) {
                            // Urči dôvod podľa toho, či je obraz čierny alebo nie
                            const reason = (blackFrameCount >= blackFrameThreshold) ? 'black-stream' : 'frozen-stream';
                            $rootScope.config.DebugJsConsoleLogEnabled && console.error('🎥 STREAM ZAMRZNUTÝ! Detekované', frozenFrameCount, 'po sebe idúcich rovnakých snímkov. Dôvod:', reason);
                            self.handleFrozenStream(reason);
                            return;
                        }
                    } else {
                        // Snímok sa zmenil → stream funguje správne
                        // Resetuj počítadlá
                        if (frozenFrameCount > 0) {
                            console.log('🎥 Stream opäť funguje normálne');
                        }
                        frozenFrameCount = 0;
                        blackFrameCount = 0;
                    }
                    
                    debugMode && $rootScope.toggleToastNotification({ show: true, text: "Black: <code>" + blackPercentage.toFixed(2) + "% </code>| Sig: <code>" + frameSignature + "</code> | Frozen: <code>" + frozenFrameCount + "</code> | Track: <code>" + (trackEnabled ? '✓' : '✗') + "</code> | Muted: <code>" + (trackMuted ? '🔇' : '🔊') + "</code> | Iter: <code>" + (iteratorCount + 1) + "</code>/<code>" + maxIterations + "</code>", id: "cameraHealthCheck-toast-combined", timeout: maxFrozenDuration / 1000 + 1 });

                    // Ulož aktuálnu signatúru pre ďalšie porovnanie
                    lastFrameData = frameSignature;
                } catch (err) {
                    // Ak nastala InvalidStateError, track už nie je aktívny
                    if (err.name === 'InvalidStateError') {
                        self.handleVideoNotAvailable('🎥 Video track prestal byť aktívny počas monitoringu');
                        return;
                    }
                    console.error('🎥 Chyba pri kontrole streamu kamery:', err);
                }
            }, checkInterval);
        };

        /**
         * Zastavenie monitoringu zamrznutého streamu
         * 
         * Volá sa automaticky:
         *   - Po detekcii zamrznutia
         *   - Po 20 sekundách od spustenia
         *   - Keď je naplánovaný reštart
         */
        self.stopFrozenStreamMonitoring = function() {
            if (!frozenMonitoringActive) {
                return; // Už nie je aktívny, netreba zastavovať
            }
            
            $rootScope.config.DebugJsConsoleLogEnabled && console.log('🎥 Zastavujem monitoring zamrznutého streamu');
            debugMode && $rootScope.toggleToastNotification({show: true, id: "cameraHealthCheck-startStop", text: "Camera health check <mark>STOPPED</mark>", timeout: 3});
            if (frozenCheckInterval) {
                clearInterval(frozenCheckInterval);
                frozenCheckInterval = null;
            }
            frozenMonitoringActive = false;
            imageCapture = null;
        };

        // Funkcia na spracovanie zamrznutého streamu
        self.handleFrozenStream = function(reason) {
            $rootScope.config.DebugJsConsoleLogEnabled && console.error('🎥 Spracovávam zamrznutý stream - inicializujem reštart. Dôvod:', reason);
            // Zastav monitoring
            self.stopFrozenStreamMonitoring();
            // Zobraz reštart notifikáciu
            self.handleNotifyAndRestart(reason || 'frozen-stream');
        };

        // Funkcia na spracovanie nedostupného videa (kamera použitá inou aplikáciou)
        self.handleVideoNotAvailable = function(reason) {
            $rootScope.config.DebugJsConsoleLogEnabled && console.error(reason);
            // Zastav monitoring
            self.stopFrozenStreamMonitoring();
            // Zobraz reštart notifikáciu
            self.handleNotifyAndRestart('stream-unavailable');
        };

        // Funkcia na manuálne zastavenie kontroly streamu kamery
        self.stop = function() {
            $rootScope.config.DebugJsConsoleLogEnabled && console.log('🎥 Zastavujem kontrolu streamu kamery');
            self.stopFrozenStreamMonitoring();
        };

        // ============================================
        // KONTROLA STAVU KAMERY PRED ODOSLANÍM FOTKY
        // ============================================
        
        /**
         * Jednoduchá kontrola, či je kamera v poriadku (nie je detekovaná porucha)
         * Používa sa pred odoslaním fotky do NativeApp
         * 
         * @returns {boolean} true = kamera je v poriadku, false = detekovaná porucha alebo naplánovaný reštart
         */
        self.isCameraHealthy = function() {
            return !self.cameraHealthCheckRestartPlanned;
        };

    });